import { ComponentProps } from 'react';
import { ReadyState } from 'react-use-websocket';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { faSparkles } from '@fortawesome/pro-solid-svg-icons';
import clsx from 'clsx';

import { tv } from '@utils';

import { Button, Icon, Tooltip } from '@components/Shared';

import { ChatOptions } from './ChatOptions';

const connectionStatusVariants = tv({
  base: 'inline-flex h-2 w-2 rounded-full hover:scale-125 transition-transform transition-duration-300',
  variants: {
    variant: {
      [ReadyState.OPEN]: 'bg-success animate-pulse',
      [ReadyState.CLOSED]: 'bg-neural-04',
      [ReadyState.CLOSING]: 'bg-neural-04 animate-pulse',
      [ReadyState.CONNECTING]: 'bg-warning animate-pulse',
      [ReadyState.UNINSTANTIATED]: 'bg-error',
    },
  },
});

const CONSISTENT_TOOLTIP_CONNECTION_STATUS = {
  [ReadyState.OPEN]: 'Connected',
  [ReadyState.CLOSED]: 'Disconnected',
  [ReadyState.CLOSING]: 'Closing...',
  [ReadyState.CONNECTING]: 'Connecting...',
  [ReadyState.UNINSTANTIATED]: 'Connection error',
};

type Props = ComponentProps<typeof ChatOptions> & {
  readyState: ReadyState;
  onCloseChat: () => void;
  toggleFullscreen: () => void;
};

export const ChatHeader = ({
  isFullscreen,
  readyState,
  onCloseChat,
  ...others
}: Props) => {
  return (
    <div
      className={clsx(
        'flex h-20 flex-none items-center justify-between border-b pl-8 pr-3',
        { 'shadow-sm': isFullscreen },
      )}
    >
      <div className="flex items-center gap-3 text-black">
        <Icon size="lg" icon={faSparkles} className="rotate-180" />
        <h2 className="flex font-[Inter] text-2xl font-semibold">Copilot</h2>

        <Tooltip>
          <div
            slot="trigger"
            className={connectionStatusVariants({
              variant: readyState,
            })}
          />

          {`Connection status: ${CONSISTENT_TOOLTIP_CONNECTION_STATUS[readyState]}`}
        </Tooltip>
      </div>

      <div className="flex items-center gap-3">
        <ChatOptions {...others} isFullscreen={isFullscreen} />

        <Button
          theme
          className="transition-opacity hover:opacity-60"
          onClick={onCloseChat}
          aria-label={
            isFullscreen ? 'Exit full screen & close chat' : 'Close chat'
          }
        >
          <Icon size="lg" icon={faXmark} className="rotate-180" />
        </Button>
      </div>
    </div>
  );
};
